import React, { Component } from "react";
import { connect } from "react-redux";
import { setPlateValue } from "./store/actions";
import ControlButton from "./controlButton";
import { injectIntl, FormattedMessage } from "react-intl";

import { TranslationsProvider } from "@learningblocks/simulations/shared-components";

import { fetchTranslations } from "./locales/config";

import styles from "./styles.module.scss";

/**
 * Predictions59 - A class component for rendering prediction controls and plate results.
 *
 * This component displays interactive controls for predicting outcomes on 
 different types of plates and renders the corresponding results based on user interaction.
 */
class Predictions59 extends Component {
  constructor(props) {
    super(props);
  }

  /**
   * Renders control buttons for adjusting predictions.
   *
   * @param {string} plate_type - The type of the plate (e.g., lb, lb-amp).
   * @param {string} plate_side - The side of the plate being controlled (e.g., pminus, pplus).
   * @param {string} button_side - The side of the button (e.g., left, right).
   * @returns {React.Component} - Rendered control buttons.
   */
  renderContolButtons(plate_type, plate_side, button_side) {
    return (
      <>
        <ControlButton
          button_type="plus"
          button_side={button_side}
          plate_type={plate_type}
          plate_side={plate_side}
          predictions={this.props.predictions}
          onClickMethod={this.onClickMethod}
        />
        <ControlButton
          button_type="minus"
          button_side={button_side}
          plate_type={plate_type}
          plate_side={plate_side}
          predictions={this.props.predictions}
          onClickMethod={this.onClickMethod}
        />
      </>
    );
  }

  /**
   * Renders a button for selecting the color of the LB/AMP/ARA plate.
   *
   * @param {string} color - The color option for the plate.
   * @returns {React.Component} - Rendered color selection button.
   */
  renderPlate3Button(color) {
    const isActive = this.props.predictions["lb-amp-ara"].type === color;

    return (
      <div
        key={color}
        role="button"
        aria-label={this.props.intl.formatMessage(
          {
            id: "Set LB/AMP/ARA plate to {color}.",
            defaultMessage: "Set LB/AMP/ARA plate to {color}.",
            description: "",
          },
          { color: color.toLowerCase() }
        )}
        className={`${styles.resultsControlsBtn} ${
          isActive ? styles.btnActive : ""
        }`}
        onClick={() =>
          this.props.setPlateValue({
            state_type: "predictions",
            plate: "lb-amp-ara",
            side: "type",
            value: color,
          })
        }
        onKeyDown={(e) => {
          if (e.keyCode === 13 || e.keyCode === 32)
            this.props.setPlateValue({
              state_type: "predictions",
              plate: "lb-amp-ara",
              side: "type",
              value: color,
            });
        }}
        tabIndex="0"
      >
        {color}
      </div>
    );
  }

  /**
   * Method to handle click events on control buttons.
   *
   * @param {string} plate - The type of the plate.
   * @param {string} side - The side of the plate being controlled.
   * @param {number} value - The current value associated with the control.
   * @param {string} type - The type of control (plus or minus).
   */
  onClickMethod = (plate, side, value, type) => {
    if (type === "plus" && plate === "lb-amp-ara" && value === 5) {
      return;
    } else if (type === "plus" && value === 6) {
      return;
    }

    if (type === "minus" && value === 0) {
      return;
    }
    this.props.setPlateValue({
      state_type: "predictions",
      plate: plate,
      side: side,
      value: type === "plus" ? value + 1 : value - 1,
    });
  };

  /**
   * Renders the result section for a specific plate type.
   *
   * @param {string} plate - The type of the plate.
   * @param {string} plate_name - The display name of the plate.
   * @returns {React.Component} - Rendered result section for the plate.
   */
  renderResult(plate, plate_name) {
    return (
      <div className={styles.sliderResult}>
        <label htmlFor="slider-plate" className={styles.resultLabel}>
          P- | P+
        </label>
        <div id="slider-plate" className={styles.sliderPlate}>
          <div>
            <img
              draggable="false"
              src={
                require(
                  `./img/pminus/${this.props.predictions[plate]["pminus"]}.svg`
                ).default
              }
              alt=""
            />
          </div>
          <div>
            <img
              draggable="false"
              src={
                require(
                  `./img/pplus/${this.props.predictions[plate]["pplus"]}.svg`
                ).default
              }
              alt=""
            />
          </div>
        </div>
        <label className={styles.label}>{plate_name}</label>
      </div>
    );
  }

  render() {
    const colors = [
      this.props.intl.formatMessage({
        id: "Red",
        defaultMessage: "Red",
        description: "",
      }),
      this.props.intl.formatMessage({
        id: "Both",
        defaultMessage: "Both",
        description: "",
      }),
      this.props.intl.formatMessage({
        id: "White",
        defaultMessage: "White",
        description: "",
      }),
    ];
    return (
      <div className={styles.predictions59}>
        <h4 className={styles.description} style={{ margin: "0px" }}>
          <FormattedMessage
            id="PREDICT"
            defaultMessage="PREDICT"
            description=""
          />
        </h4>
        <p>
          <FormattedMessage
            id="In the plates below, predict the growth level and colony color in each condition by increasing or decreasing bacterial growth."
            defaultMessage="In the plates below, predict the growth level and colony color in each condition by increasing or decreasing bacterial growth.In the plates below, predict the growth level and colony color in each condition by increasing or decreasing bacterial growth."
            description=""
          />
        </p>

        <div className={styles.wrapper}>
          <div className={styles.slider}>
            <div>{this.renderContolButtons("lb", "pminus", "left")}</div>
            {this.renderResult(
              "lb",
              this.props.intl.formatMessage({
                id: "LB plate",
                defaultMessage: "LB plate",
                description: "",
              })
            )}
            <div>{this.renderContolButtons("lb", "pplus", "right")}</div>
          </div>

          <div className={styles.slider}>
            <div>{this.renderContolButtons("lb-amp", "pminus", "left")}</div>

            {this.renderResult(
              "lb-amp",
              this.props.intl.formatMessage({
                id: "LB/amp plate",
                defaultMessage: "LB/amp plate",
                description: "",
              })
            )}

            <div>{this.renderContolButtons("lb-amp", "pplus", "right")}</div>
          </div>

          <div className={styles.slider}>
            <div>{this.renderContolButtons("lb-amp-ara", "pplus", "left")}</div>

            <div
              className={styles.sliderResult}
              style={{
                padding: "0px 0px 7px",
                position: "relative",
              }}
            >
              {/* Render Plate 3 Color Selectors */}
              <div className={styles.resultControls}>
                {colors.map((color) => this.renderPlate3Button(color))}
              </div>

              <label
                htmlFor="slider-plate1"
                style={{ margin: "17px 0 7px 0", display: "block" }}
              >
                P+
              </label>
              <div
                id="slider-plate1"
                className={styles.sliderPlate}
                style={{
                  padding: "0px 15px",
                }}
              >
                <div>
                  <img
                    style={{ height: "107px", width: "104px" }}
                    draggable="false"
                    src={
                      require(
                        `./img/lb-amp-ara/${
                          this.props.predictions["lb-amp-ara"]["pplus"] == 0
                            ? "default"
                            : this.props.predictions["lb-amp-ara"]["type"] +
                              "-" +
                              this.props.predictions["lb-amp-ara"]["pplus"]
                        }.svg`
                      ).default
                    }
                    alt=""
                  />
                </div>
              </div>
              <div>
                <FormattedMessage
                  id="LB/amp/ara plate"
                  defaultMessage="LB/amp/ara plate"
                  description=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Maps Redux state to component props.
 *
 * @param {Object} state - The current state of the Redux store.
 * @returns {Object} - The mapped props.
 */
function mapStateToProps(state) {
  return {
    predictions: state.petridish.predictions,
  };
}

const Predictions59WithIntl = injectIntl(Predictions59);

export default connect(mapStateToProps, { setPlateValue })((props) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <Predictions59WithIntl {...props} />
  </TranslationsProvider>
));
